export default `  :root {
    --bg: #0A0A0A;
    --bg-elev: #131313;
    --bg-card: #1A1A1A;
    --border: #232323;
    --border-strong: #333;
    --green: #A3FF12;
    --green-dim: #8FB800;
    --blue: #3D3DFF;
    --text: #FAFAFA;
    --text-dim: #9A9A9A;
    --text-muted: #5A5A5A;
    --red: #FF3D3D;
    --orange: #FF9500;
    --amber: #FFB800;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    background: var(--bg);
    color: var(--text);
    font-family: 'Open Sans', -apple-system, sans-serif;
    font-weight: 400;
    line-height: 1.5;
    overflow-x: hidden;
  }
  a { color: var(--green); text-decoration: none; }
  a:hover { text-decoration: underline; }

  /* HEADER */
  .topbar {
    border-bottom: 1px solid var(--border);
    padding: 18px 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    background: rgba(10,10,10,0.92);
    backdrop-filter: blur(12px);
    z-index: 100;
  }
  .logo {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 22px;
    letter-spacing: 0.5px;
    color: var(--green);
    text-transform: uppercase;
  }
  .topbar-meta { display: flex; gap: 24px; align-items: center; font-size: 12px; color: var(--text-dim); font-family: 'JetBrains Mono', monospace; }
  .topbar-meta .badge { padding: 4px 10px; border: 1px solid var(--green); color: var(--green); border-radius: 2px; }

  /* LAYOUT */
  .container { max-width: 1400px; margin: 0 auto; padding: 0 32px; }
  section { padding: 80px 0; border-bottom: 1px solid var(--border); }
  .section-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--green);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 12px;
  }

  /* HERO */
  .hero { padding: 100px 0 80px; border-bottom: 1px solid var(--border); position: relative; overflow: hidden; }
  .hero::before {
    content: '';
    position: absolute;
    top: -200px;
    right: -100px;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(163,255,18,0.08), transparent 70%);
    pointer-events: none;
  }
  .hero h1 {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: clamp(48px, 8vw, 120px);
    line-height: 0.92;
    text-transform: uppercase;
    letter-spacing: -1.5px;
    margin-bottom: 28px;
  }
  .hero h1 .accent { color: var(--green); font-style: italic; }
  .hero p.subtitle {
    font-size: 18px;
    color: var(--text-dim);
    max-width: 720px;
    margin-bottom: 60px;
    line-height: 1.5;
  }

  /* KPI ROW */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
  }
  .kpi {
    padding: 32px 28px;
    border-right: 1px solid var(--border);
    position: relative;
  }
  .kpi:last-child { border-right: none; }
  .kpi-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 8px;
  }
  .kpi-value {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 60px;
    line-height: 1;
    color: var(--green);
    margin-bottom: 4px;
  }
  .kpi-sub {
    font-size: 13px;
    color: var(--text-dim);
  }

  /* SECTION HEADINGS */
  h2 {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: clamp(34px, 5vw, 60px);
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: -0.5px;
    margin-bottom: 16px;
  }
  h2 .accent { color: var(--green); }
  .section-lede {
    font-size: 17px;
    color: var(--text-dim);
    max-width: 760px;
    margin-bottom: 48px;
    line-height: 1.55;
  }

  /* PILLAR CARDS */
  .pillar-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 40px;
  }
  .pillar-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    padding: 28px;
    position: relative;
    transition: border-color 0.2s;
  }
  .pillar-card:hover { border-color: var(--green); }
  .pillar-card .pillar-name {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 19px;
    text-transform: uppercase;
    margin-bottom: 6px;
    line-height: 1.1;
  }
  .pillar-card .pillar-tag {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: var(--green);
    margin-bottom: 24px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .pillar-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 12px; }
  .pillar-stat-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); text-transform: uppercase; }
  .pillar-stat-value { font-family: 'Rubik', sans-serif; font-weight: 900; font-size: 26px; line-height: 1; }

  /* CHARTS */
  .chart-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  .chart-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    padding: 28px;
  }
  .chart-card h3 {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 20px;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .chart-card .chart-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--text-dim);
    margin-bottom: 24px;
  }
  .chart-wrap { position: relative; height: 320px; }
  .chart-wrap-tall { position: relative; height: 420px; }

  /* TABLE */
  .table-wrap { overflow-x: auto; background: var(--bg-card); border: 1px solid var(--border); }
  table { width: 100%; border-collapse: collapse; font-family: 'Open Sans', sans-serif; font-size: 13px; }
  thead { background: #0A0A0A; }
  th {
    text-align: left;
    padding: 16px 14px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-dim);
    border-bottom: 1px solid var(--border-strong);
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
  }
  th:hover { color: var(--green); }
  th .sort-ind { opacity: 0.4; margin-left: 4px; }
  th.sorted .sort-ind { opacity: 1; color: var(--green); }
  th.num-th { text-align: right; }
  th .th-main { display: block; }
  th .th-sub {
    display: block;
    font-family: 'Open Sans', sans-serif;
    font-size: 10px;
    color: var(--text-muted);
    text-transform: none;
    letter-spacing: 0;
    margin-top: 4px;
    font-weight: 400;
    line-height: 1.3;
  }
  th.num-th .th-sub { text-align: right; }
  td {
    padding: 16px 14px;
    border-bottom: 1px solid var(--border);
    vertical-align: middle;
  }
  tr:hover td { background: rgba(163,255,18,0.03); }
  tr.expanded td { background: rgba(163,255,18,0.05); }
  td.handle { font-weight: 700; }
  td.handle .creator-pillar {
    display: block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-top: 4px;
    font-weight: 400;
  }
  td.num { font-family: 'JetBrains Mono', monospace; text-align: right; }
  .tier-badge {
    display: inline-block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    padding: 3px 8px;
    border: 1px solid var(--border-strong);
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-left: 8px;
  }
  .sent-bar {
    display: flex;
    height: 8px;
    width: 100px;
    background: var(--border);
    overflow: hidden;
  }
  .sent-bar .pos { background: var(--green); }
  .sent-bar .neu { background: #555; }
  .sent-bar .neg { background: var(--red); }
  .gap-cell { color: var(--text-muted); font-style: italic; font-family: 'JetBrains Mono', monospace; font-size: 11px; }

  /* expandable comment rows */
  tr.detail-row td {
    padding: 0;
    background: #0A0A0A;
    border-bottom: 1px solid var(--border);
  }
  .detail-inner { padding: 24px 32px; max-height: 520px; overflow-y: auto; }
  .detail-inner::-webkit-scrollbar { width: 6px; }
  .detail-inner::-webkit-scrollbar-thumb { background: var(--border-strong); }

  /* METRIC STRIP inside detail row */
  .metric-strip {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 0;
    padding: 4px 0 20px;
    margin-bottom: 18px;
    border-bottom: 1px dashed var(--border-strong);
  }
  .metric-cell {
    border-left: 1px solid var(--border);
    padding: 4px 12px;
  }
  .metric-cell:first-child { border-left: none; padding-left: 0; }
  .metric-cell .m-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
  }
  .metric-cell .m-value {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 22px;
    line-height: 1;
    color: var(--green);
  }
  .metric-cell .m-sub {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    color: var(--text-muted);
    margin-top: 6px;
    letter-spacing: 0.5px;
  }
  @media (max-width: 900px) {
    .metric-strip { grid-template-columns: repeat(2, 1fr); gap: 12px 0; }
    .metric-cell { border-left: none; padding: 0 12px; }
  }

  .comment-line {
    display: flex;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px dashed var(--border);
    font-size: 13px;
  }
  .comment-line:last-child { border-bottom: none; }
  .comment-meta { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-muted); min-width: 90px; }
  .comment-text { flex: 1; color: var(--text); }
  .comment-tags { display: flex; gap: 6px; font-family: 'JetBrains Mono', monospace; font-size: 9px; text-transform: uppercase; flex-shrink: 0; }
  .tag-POS { background: rgba(163,255,18,0.15); color: var(--green); padding: 2px 6px; }
  .tag-NEU { background: rgba(255,255,255,0.05); color: var(--text-dim); padding: 2px 6px; }
  .tag-NEG { background: rgba(255,61,61,0.15); color: var(--red); padding: 2px 6px; }
  .tag-theme { background: rgba(61,61,255,0.15); color: #8888FF; padding: 2px 6px; }

  /* PILLAR FILTER */
  .pillar-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 32px;
  }
  .pillar-btn {
    background: transparent;
    border: 1px solid var(--border-strong);
    color: var(--text-dim);
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 10px 16px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .pillar-btn:hover { border-color: var(--green); color: var(--text); }
  .pillar-btn.active { background: var(--green); color: #000; border-color: var(--green); font-weight: 700; }

  /* DEEP DIVE */
  .deepdive-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-color: var(--green);
    padding: 36px;
    max-width: 980px;
    margin: 0 auto;
  }
  .verdict-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 10px;
    color: var(--green);
  }
  .deepdive-handle {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 42px;
    line-height: 1;
    margin-bottom: 8px;
  }
  .deepdive-meta {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--text-dim);
    margin-bottom: 18px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .deepdive-numbers {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin: 24px 0;
  }
  .deepdive-num { border: 1px solid var(--border); padding: 14px; }
  .deepdive-num-value { font-family: 'Rubik', sans-serif; font-weight: 900; font-size: 28px; line-height: 1; color: var(--green); }
  .deepdive-num-label { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-dim); text-transform: uppercase; margin-top: 6px; }
  .takeaway { font-size: 15px; color: var(--text-dim); margin-top: 18px; line-height: 1.6; }
  .quote-list { display: flex; flex-direction: column; gap: 10px; max-height: 380px; overflow-y: auto; padding-right: 8px; margin-top: 18px; }
  .quote-list::-webkit-scrollbar { width: 6px; }
  .quote-list::-webkit-scrollbar-thumb { background: var(--border-strong); }
  .quote {
    padding: 14px 16px;
    border-left: 2px solid var(--green);
    background: rgba(163,255,18,0.04);
    font-size: 14px;
  }
  .quote.neg { border-left-color: var(--red); background: rgba(255,61,61,0.04); }
  .quote.neu { border-left-color: var(--text-muted); background: rgba(255,255,255,0.02); }
  .quote-author { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-dim); margin-top: 6px; }

  /* PILLAR EFFICIENCY CARDS */
  .efficiency-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 32px;
  }
  .eff-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    padding: 24px;
  }
  .eff-card.winner { border-color: var(--green); }
  .eff-card.below { border-color: var(--red); }
  .eff-card.par { border-color: var(--orange); }
  .eff-card .eff-name {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 8px;
    line-height: 1.3;
    min-height: 28px;
  }
  .eff-card .eff-headline {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 44px;
    line-height: 1;
    margin-bottom: 4px;
  }
  .eff-card.winner .eff-headline { color: var(--green); }
  .eff-card.par .eff-headline { color: var(--orange); }
  .eff-card.below .eff-headline { color: var(--red); }
  .eff-card .eff-sub { font-size: 13px; color: var(--text-dim); margin-top: 8px; }
  .eff-card .eff-detail { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-dim); }
  .eff-card .eff-detail span strong { color: var(--text); font-weight: 700; }

  /* COMPOSITION LAYOUT */
  .composition-layout {
    display: grid;
    grid-template-columns: 1.5fr 1fr;
    gap: 16px;
    margin-top: 16px;
  }
  .pillar-callout-stack { display: flex; flex-direction: column; gap: 12px; }
  .pillar-callout {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-left: 4px solid var(--green);
    padding: 20px 24px;
  }
  .pillar-callout.bad { border-left-color: var(--red); }
  .pillar-callout.mid { border-left-color: var(--orange); }
  .pillar-callout .pc-name {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--green);
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 8px;
  }
  .pillar-callout.bad .pc-name { color: var(--red); }
  .pillar-callout.mid .pc-name { color: var(--orange); }
  .pillar-callout .pc-headline {
    font-family: 'Rubik', sans-serif; font-weight: 900;
    font-size: 48px;
    line-height: 1;
    margin-bottom: 4px;
  }
  .pillar-callout .pc-stat {
    font-size: 14px;
    color: var(--text);
    margin-bottom: 6px;
  }
  .pillar-callout .pc-read {
    font-size: 13px;
    color: var(--text-dim);
    line-height: 1.45;
  }

  /* CUSTOM LEGEND */
  .custom-legend {
    display: flex;
    gap: 22px;
    justify-content: center;
    margin: 24px 0 0;
    flex-wrap: wrap;
  }
  .legend-item {
    cursor: help;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text);
  }
  .legend-dot { width: 12px; height: 12px; display: inline-block; border-radius: 2px; }

  /* HARD TRUTH CALLOUT */
  .truth-block {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-left: 4px solid var(--red);
    padding: 28px 32px;
    margin-top: 40px;
  }
  .truth-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: var(--red);
    text-transform: uppercase;
    letter-spacing: 1.5px;
    margin-bottom: 12px;
  }
  .truth-block p {
    font-size: 15px;
    color: var(--text);
    line-height: 1.6;
    margin-bottom: 8px;
  }
  .truth-block p:last-child { margin-bottom: 0; color: var(--text-dim); }

  /* FOOTER */
  footer { padding: 60px 32px; text-align: center; color: var(--text-muted); font-size: 12px; font-family: 'JetBrains Mono', monospace; border-top: 1px solid var(--border); }
  footer .footer-logo { font-family: 'Rubik', sans-serif; font-weight: 900; font-size: 28px; color: var(--green); margin-bottom: 12px; text-transform: uppercase; }
  footer .footer-tag { color: var(--text-dim); margin-bottom: 8px; }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .kpi-grid { grid-template-columns: repeat(2, 1fr); }
    .kpi:nth-child(2) { border-right: none; }
    .pillar-grid { grid-template-columns: 1fr; }
    .chart-grid { grid-template-columns: 1fr; }
    .composition-layout { grid-template-columns: 1fr; }
    .efficiency-grid { grid-template-columns: 1fr; }
    .deepdive-numbers { grid-template-columns: 1fr 1fr; }
    .container { padding: 0 20px; }
    .topbar { padding: 14px 20px; }
  }
`;



// WEBPACK FOOTER //
// ./src/components/reports/kobe/style.js